import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { api, useAuth } from '../App';
import Modal from '../components/UI/Modal';
import StatusPill from '../components/UI/StatusPill';
import { money } from '../utils/format';
import { hasPermission, canViewCosts, protectedMoney } from '../utils/permissions';
import ProductDetail from '../components/UI/ProductDetail';
import { Search, Plus, SlidersHorizontal, ChevronUp, ChevronDown, ChevronsUpDown, ShoppingCart, Check } from 'lucide-react';

export default function Inventory() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [searchParams] = useSearchParams();
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [locations, setLocations] = useState([]);
  const [catFilter, setCatFilter] = useState('');
  const [locFilter, setLocFilter] = useState('');
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [countModal, setCountModal] = useState(null);
  const [wasteModal, setWasteModal] = useState(null);
  const [countQty, setCountQty] = useState('');
  const [countNotes, setCountNotes] = useState('');
  const [wasteQty, setWasteQty] = useState('');
  const [wasteReason, setWasteReason] = useState('breakage');
  const [wasteNotes, setWasteNotes] = useState('');
  const [parModal, setParModal] = useState(null);
  const [parForm, setParForm] = useState({ par_min:'', par_max:'', reorder_point:'', reorder_qty:'' });
  const [sortCol, setSortCol] = useState(null);
  const [sortDir, setSortDir] = useState('asc');
  const [selected, setSelected] = useState(new Set());
  const [viewProductId, setViewProductId] = useState(null);
  const [orderModal, setOrderModal] = useState(null);

  // Read URL params reactively
  useEffect(() => {
    const s = searchParams.get('status');
    const q = searchParams.get('search');
    const l = searchParams.get('location_id');
    const cat = searchParams.get('category_id');
    if (s) setStatusFilter(s);
    if (q) setSearch(q);
    if (l) setLocFilter(l);
    if (cat) setCatFilter(cat);
  }, [searchParams]);

  const load = () => {
    const params = new URLSearchParams();
    if (locFilter) params.set('location_id', locFilter);
    if (catFilter) params.set('category_id', catFilter);
    if (search) params.set('search', search);
    if (statusFilter) params.set('status', statusFilter);
    api('/api/inventory?' + params).then(r => { setItems(r.data); setSelected(new Set()); }).catch(console.error);
  };

  useEffect(() => { api('/api/categories').then(r => setCategories(r.data)).catch(() => {}); api('/api/locations').then(r => setLocations(r.data)).catch(() => {}); }, []);
  useEffect(() => { load(); }, [catFilter, locFilter, search, statusFilter]);

  const showCosts = canViewCosts(user);
  const canCount = hasPermission(user, 'count_inventory');
  const canWaste = hasPermission(user, 'log_waste');
  const canEditPar = hasPermission(user, 'edit_par_levels');

  const toggleSort = (col) => { if (sortCol === col) setSortDir(d => d === 'asc' ? 'desc' : 'asc'); else { setSortCol(col); setSortDir('asc'); } };
  const SortIcon = ({ col }) => {
    if (sortCol !== col) return <ChevronsUpDown size={12} style={{ opacity: .3, marginLeft: 4 }} />;
    return sortDir === 'asc' ? <ChevronUp size={12} style={{ color: 'var(--gold)', marginLeft: 4 }} /> : <ChevronDown size={12} style={{ color: 'var(--gold)', marginLeft: 4 }} />;
  };

  const sorted = useMemo(() => {
    if (!sortCol) return items;
    const copy = [...items];
    copy.sort((a, b) => {
      switch (sortCol) {
        case 'product': { const cmp = (a.product_name||'').localeCompare(b.product_name||''); return sortDir === 'asc' ? cmp : -cmp; }
        case 'location': { const cmp = (a.location_name||'').localeCompare(b.location_name||''); return sortDir === 'asc' ? cmp : -cmp; }
        case 'category': { const cmp = (a.category_name||'').localeCompare(b.category_name||''); return sortDir === 'asc' ? cmp : -cmp; }
        case 'stock': return sortDir === 'asc' ? Number(a.current_stock)-Number(b.current_stock) : Number(b.current_stock)-Number(a.current_stock);
        case 'par_min': return sortDir === 'asc' ? Number(a.par_min)-Number(b.par_min) : Number(b.par_min)-Number(a.par_min);
        case 'par_max': return sortDir === 'asc' ? Number(a.par_max)-Number(b.par_max) : Number(b.par_max)-Number(a.par_max);
        case 'reorder': return sortDir === 'asc' ? Number(a.reorder_point)-Number(b.reorder_point) : Number(b.reorder_point)-Number(a.reorder_point);
        case 'status': { const o={reorder:0,low:1,ok:2}; return sortDir==='asc' ? (o[a.stock_status]??3)-(o[b.stock_status]??3) : (o[b.stock_status]??3)-(o[a.stock_status]??3); }
        default: return 0;
      }
    });
    return copy;
  }, [items, sortCol, sortDir]);

  const toggleSelect = (id) => setSelected(prev => { const n = new Set(prev); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  const selectAll = () => { if (selected.size === sorted.length) setSelected(new Set()); else setSelected(new Set(sorted.map(i => i.id))); };

  // Build POs grouped by (location, category) — user picks supplier per group
  const generateOrders = async () => {
    const selectedItems = sorted.filter(i => selected.has(i.id));
    if (!selectedItems.length) return;

    const [prodRes, supRes] = await Promise.all([api('/api/products'), api('/api/suppliers')]);
    const prodMap = {};
    prodRes.data.forEach(p => { prodMap[p.id] = p; });
    const allSuppliers = supRes.data;

    // Group by (location_id, category_id)
    const poGroups = {};
    for (const item of selectedItems) {
      const prod = prodMap[item.product_id];
      const catId = item.category_id || (prod && prod.category_id);
      if (!catId) continue;
      const key = item.location_id + '-' + catId;
      if (!poGroups[key]) {
        // Find suppliers serving this category
        const catSuppliers = allSuppliers.filter(s =>
          (s.categories || []).some(sc => sc.id === catId)
        );
        // Fall back to product's default supplier if no category match
        if (!catSuppliers.length && prod && prod.default_supplier_id) {
          const defSup = allSuppliers.find(s => s.id === prod.default_supplier_id);
          if (defSup) catSuppliers.push(defSup);
        }
        const defaultSup = (prod && prod.default_supplier_id)
          ? catSuppliers.find(s => s.id === prod.default_supplier_id) || catSuppliers[0]
          : catSuppliers[0];

        poGroups[key] = {
          supplier_id: defaultSup ? defaultSup.id : null,
          supplier_name: defaultSup ? defaultSup.name : '',
          available_suppliers: catSuppliers,
          category_name: item.category_name || '',
          location_id: item.location_id,
          location_name: item.location_name,
          items: []
        };
      }
      const qtyNeeded = Math.max(1, Math.round(Number(item.par_max) - Number(item.current_stock)));
      poGroups[key].items.push({
        product_id: item.product_id, product_name: item.product_name,
        sku: item.sku, unit_type: item.unit_type,
        qty_ordered: qtyNeeded, unit_cost: Number(item.unit_cost) || 0,
      });
    }

    const groups = Object.values(poGroups).filter(g => g.available_suppliers.length > 0);
    if (!groups.length) { alert('No suppliers found for selected items. Assign suppliers to product categories first.'); return; }
    setOrderModal({ groups, currentIndex: 0 });
  };

  const changeGroupSupplier = (supplierId) => {
    const groups = [...orderModal.groups];
    const g = groups[orderModal.currentIndex];
    const sup = g.available_suppliers.find(s => s.id === parseInt(supplierId));
    g.supplier_id = parseInt(supplierId);
    g.supplier_name = sup ? sup.name : '';
    setOrderModal({ ...orderModal, groups });
  };

  const submitOrder = async () => {
    const g = orderModal.groups[orderModal.currentIndex];
    try {
      await api('/api/orders', {
        method: 'POST',
        body: JSON.stringify({
          location_id: g.location_id, supplier_id: g.supplier_id,
          items: g.items.map(i => ({ product_id: i.product_id, qty_ordered: i.qty_ordered, unit_cost: i.unit_cost })),
        })
      });
      if (orderModal.currentIndex < orderModal.groups.length - 1) {
        setOrderModal({ ...orderModal, currentIndex: orderModal.currentIndex + 1 });
      } else {
        setOrderModal(null); setSelected(new Set());
        alert(orderModal.groups.length + ' PO(s) created successfully');
        load();
      }
    } catch (err) { alert(err.error || 'Failed to create order'); }
  };

  const submitCount = async () => {
    try { await api('/api/inventory/count', { method:'POST', body:JSON.stringify({ product_location_id:countModal.id, count_qty:parseFloat(countQty), notes:countNotes }) }); setCountModal(null); setCountQty(''); setCountNotes(''); load(); } catch(err) { alert(err.error||'Failed'); }
  };
  const submitWaste = async () => {
    try { await api('/api/inventory/waste', { method:'POST', body:JSON.stringify({ product_location_id:wasteModal.id, quantity:parseFloat(wasteQty), reason:wasteReason, notes:wasteNotes }) }); setWasteModal(null); setWasteQty(''); setWasteNotes(''); load(); } catch(err) { alert(err.error||'Failed'); }
  };
  const openPar = (item) => {
    setParModal(item);
    setParForm({ par_min: Number(item.par_min), par_max: Number(item.par_max), reorder_point: Number(item.reorder_point), reorder_qty: Number(item.reorder_qty || 0) });
  };

  const submitPar = async () => {
    try {
      await api('/api/inventory/par/' + parModal.id, { method: 'PUT', body: JSON.stringify({
        par_min: parseFloat(parForm.par_min), par_max: parseFloat(parForm.par_max),
        reorder_point: parseFloat(parForm.reorder_point), reorder_qty: parseFloat(parForm.reorder_qty)
      }) });
      setParModal(null); load();
    } catch (err) { alert(err.error || 'Failed'); }
  };

  const generatePOs = async () => {
    if (!locFilter) return alert('Select a location first');
    try { const r = await api('/api/reorder/generate', { method:'POST', body:JSON.stringify({ location_id:parseInt(locFilter) }) }); alert(r.message||'Done'); } catch(err) { alert(err.error||'Failed'); }
  };

  const thSort = (label, col) => (
    <th onClick={() => toggleSort(col)} style={{ cursor:'pointer', userSelect:'none', whiteSpace:'nowrap' }}>
      <span style={{ display:'inline-flex', alignItems:'center' }}>{label}<SortIcon col={col} /></span>
    </th>
  );

  const currentGroup = orderModal ? orderModal.groups[orderModal.currentIndex] : null;

  return (
    <div>
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:24, flexWrap:'wrap', gap:12 }}>
        <h1 style={{ fontFamily:'Lora,serif', fontSize:26 }}>Inventory</h1>
        <div style={{ display:'flex', gap:8, flexWrap:'wrap' }}>
          {selected.size > 0 && (
            <button className="btn btn-primary" onClick={generateOrders}>
              <ShoppingCart size={16} /> Order {selected.size} Item{selected.size > 1 ? 's' : ''}
            </button>
          )}
          <button className="btn btn-secondary" onClick={generatePOs}><Plus size={16} /> Auto-Generate POs</button>
        </div>
      </div>

      <div className="tabs">
        <button className={'tab'+(!catFilter?' active':'')} onClick={() => setCatFilter('')}>All</button>
        {categories.map(c => (
          <button key={c.id} className={'tab'+(catFilter==c.id?' active':'')} onClick={() => setCatFilter(c.id)} style={catFilter==c.id?{}:{borderBottom:'2px solid '+c.color}}>
            {c.name.split(' ')[0]}
          </button>
        ))}
      </div>

      <div className="filters">
        <select value={locFilter} onChange={e => setLocFilter(e.target.value)}>
          <option value="">All Locations</option>
          {locations.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
        </select>
        <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
          <option value="">All Status</option>
          <option value="reorder">Reorder</option>
          <option value="low">Low</option>
          <option value="ok">OK</option>
        </select>
        <div className="search-input"><Search size={16} /><input placeholder="Search products..." value={search} onChange={e => setSearch(e.target.value)} /></div>
      </div>

      <div className="table-wrap">
        <table>
          <thead><tr>
            <th style={{ width:40 }}><input type="checkbox" checked={selected.size===sorted.length && sorted.length>0} onChange={selectAll} style={{ width:'auto', accentColor:'var(--gold)' }} /></th>
            {thSort('Product','product')}{thSort('Location','location')}{thSort('Category','category')}{thSort('Stock','stock')}{thSort('Par Min','par_min')}{thSort('Par Max','par_max')}{thSort('Reorder Pt','reorder')}{thSort('Status','status')}<th>Actions</th>
          </tr></thead>
          <tbody>
            {sorted.map(item => (
              <tr key={item.id} style={{ background:selected.has(item.id)?'var(--gold-pale)':undefined }}>
                <td><input type="checkbox" checked={selected.has(item.id)} onChange={() => toggleSelect(item.id)} style={{ width:'auto', accentColor:'var(--gold)' }} /></td>
                <td><div style={{ fontWeight:600, cursor:'pointer', borderBottom:'1px dashed var(--faint)', display:'inline' }} onClick={() => setViewProductId(item.product_id)} onMouseEnter={e => e.currentTarget.style.color='var(--gold)'} onMouseLeave={e => e.currentTarget.style.color='var(--text)'}>{item.product_name}</div><div style={{ fontSize:11, color:'var(--faint)' }}>{item.sku} - {item.unit_type}</div></td>
                <td>{item.location_name}</td>
                <td><span className="cat-badge" style={{ background:item.category_color+'20', color:item.category_color }}>{item.category_name}</span></td>
                <td style={{ fontWeight:700 }}>{Number(item.current_stock).toFixed(0)}</td>
                <td>{Number(item.par_min).toFixed(0)}</td>
                <td>{Number(item.par_max).toFixed(0)}</td>
                <td>{Number(item.reorder_point).toFixed(0)}</td>
                <td><StatusPill status={item.stock_status} /></td>
                <td><div style={{ display:'flex', gap:6 }}>
                  {canCount && <button className="btn btn-primary btn-sm" onClick={() => { setCountModal(item); setCountQty(item.current_stock); }}>Count</button>}
                  {canWaste && <button className="btn btn-secondary btn-sm" onClick={() => setWasteModal(item)}>Waste</button>}
                  {canEditPar && <button className="btn btn-secondary btn-sm" onClick={() => openPar(item)} title="Edit Par Levels"><SlidersHorizontal size={13} /></button>}
                </div></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {sorted.length===0 && <p style={{ padding:20, textAlign:'center', color:'var(--muted)' }}>No inventory items found</p>}

      {/* ── Order Creation Modal with Supplier Selector ── */}
      {orderModal && currentGroup && (
        <Modal title={'Create PO — ' + currentGroup.category_name} onClose={() => setOrderModal(null)}>
          <div style={{ fontSize:13, color:'var(--muted)', marginBottom:16 }}>
            PO {orderModal.currentIndex+1} of {orderModal.groups.length} &middot; {currentGroup.location_name}
          </div>

          <div className="form-group" style={{ marginBottom:16 }}>
            <label>Supplier {currentGroup.available_suppliers.length > 1 && <span style={{ fontSize:11, color:'var(--faint)', fontWeight:400 }}>({currentGroup.available_suppliers.length} available)</span>}</label>
            {currentGroup.available_suppliers.length > 1 ? (
              <select value={currentGroup.supplier_id || ''} onChange={e => changeGroupSupplier(e.target.value)}>
                {currentGroup.available_suppliers.map(s => (
                  <option key={s.id} value={s.id}>{s.name}</option>
                ))}
              </select>
            ) : (
              <div style={{ padding:'10px 14px', background:'var(--navy)', borderRadius:8, border:'1px solid var(--border)', fontSize:14, fontWeight:500 }}>
                {currentGroup.supplier_name || 'No supplier'}
              </div>
            )}
          </div>

          <div className="table-wrap" style={{ marginBottom:16 }}>
            <table>
              <thead><tr><th>Product</th><th>SKU</th><th>Qty</th>{showCosts && <th>Unit Cost</th>}{showCosts && <th>Total</th>}</tr></thead>
              <tbody>
                {currentGroup.items.map((item, i) => (
                  <tr key={i}>
                    <td style={{ fontWeight:600 }}>{item.product_name}</td>
                    <td style={{ color:'var(--faint)' }}>{item.sku}</td>
                    <td><input type="number" value={item.qty_ordered} min="1" onChange={e => {
                      const groups = [...orderModal.groups];
                      groups[orderModal.currentIndex].items[i].qty_ordered = parseInt(e.target.value) || 1;
                      setOrderModal({ ...orderModal, groups });
                    }} style={{ width:70, textAlign:'center' }} /></td>
                    {showCosts && <td>{money(item.unit_cost)}</td>}
                    {showCosts && <td style={{ fontWeight:600 }}>{money(item.qty_ordered * item.unit_cost)}</td>}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div style={{ textAlign:'right', fontWeight:700, fontSize:16, color:'var(--gold)', marginBottom:16 }}>
            Total: {showCosts ? money(currentGroup.items.reduce((s, i) => s + i.qty_ordered * i.unit_cost, 0)) : '•••'}
          </div>
          <div className="modal-actions">
            <button className="btn btn-secondary" onClick={() => setOrderModal(null)}>Cancel</button>
            <button className="btn btn-primary" onClick={submitOrder} disabled={!currentGroup.supplier_id}>
              <Check size={14} /> Create PO{orderModal.groups.length > 1 ? ' & Next' : ''}
            </button>
          </div>
        </Modal>
      )}

      {countModal && (
        <Modal title="Log Inventory Count" onClose={() => setCountModal(null)}>
          <p style={{ color:'var(--muted)', marginBottom:16 }}>{countModal.product_name} at {countModal.location_name}</p>
          <div className="form-group"><label>Current Count</label><input type="number" value={countQty} onChange={e => setCountQty(e.target.value)} min="0" step="0.1" /></div>
          <div className="form-group"><label>Notes</label><textarea value={countNotes} onChange={e => setCountNotes(e.target.value)} rows={3} /></div>
          <div className="modal-actions"><button className="btn btn-secondary" onClick={() => setCountModal(null)}>Cancel</button><button className="btn btn-primary" onClick={submitCount}>Save Count</button></div>
        </Modal>
      )}

      {wasteModal && (
        <Modal title="Log Waste" onClose={() => setWasteModal(null)}>
          <p style={{ color:'var(--muted)', marginBottom:16 }}>{wasteModal.product_name} at {wasteModal.location_name}</p>
          <div className="form-group"><label>Quantity</label><input type="number" value={wasteQty} onChange={e => setWasteQty(e.target.value)} min="0.1" step="0.1" /></div>
          <div className="form-group"><label>Reason</label><select value={wasteReason} onChange={e => setWasteReason(e.target.value)}>
            <option value="breakage">Breakage</option><option value="expired">Expired</option><option value="spilled">Spilled</option><option value="stolen">Stolen/Missing</option><option value="comp">Comp</option><option value="other">Other</option>
          </select></div>
          <div className="form-group"><label>Notes</label><textarea value={wasteNotes} onChange={e => setWasteNotes(e.target.value)} rows={3} /></div>
          <div className="modal-actions"><button className="btn btn-secondary" onClick={() => setWasteModal(null)}>Cancel</button><button className="btn btn-danger" onClick={submitWaste}>Log Waste</button></div>
        </Modal>
      )}
      {viewProductId && <ProductDetail productId={viewProductId} onClose={() => setViewProductId(null)} />}
      {parModal && (
        <Modal title="Edit Par Levels" onClose={() => setParModal(null)}>
          <div style={{ padding:'14px 16px', background:'var(--navy)', borderRadius:10, border:'1px solid var(--border)', marginBottom:20 }}>
            <div style={{ fontWeight:600, fontSize:15, marginBottom:2 }}>{parModal.product_name}</div>
            <div style={{ fontSize:13, color:'var(--muted)' }}>{parModal.location_name}</div>
            <div style={{ fontSize:12, color:'var(--faint)', marginTop:2 }}>{parModal.sku} &middot; Current stock: {Number(parModal.current_stock).toFixed(0)}</div>
          </div>
          <div className="grid-2">
            <div className="form-group"><label>Par Min</label><input type="number" value={parForm.par_min} onChange={e => setParForm({...parForm, par_min:e.target.value})} min="0" step="1" /><div style={{ fontSize:11, color:'var(--faint)', marginTop:2 }}>Minimum safe level</div></div>
            <div className="form-group"><label>Par Max</label><input type="number" value={parForm.par_max} onChange={e => setParForm({...parForm, par_max:e.target.value})} min="0" step="1" /><div style={{ fontSize:11, color:'var(--faint)', marginTop:2 }}>Target stock level</div></div>
          </div>
          <div className="grid-2">
            <div className="form-group"><label>Reorder Point</label><input type="number" value={parForm.reorder_point} onChange={e => setParForm({...parForm, reorder_point:e.target.value})} min="0" step="1" /><div style={{ fontSize:11, color:'var(--faint)', marginTop:2 }}>Triggers alert at this level</div></div>
            <div className="form-group"><label>Reorder Qty</label><input type="number" value={parForm.reorder_qty} onChange={e => setParForm({...parForm, reorder_qty:e.target.value})} min="0" step="1" /><div style={{ fontSize:11, color:'var(--faint)', marginTop:2 }}>How many to order</div></div>
          </div>
          <div className="modal-actions"><button className="btn btn-secondary" onClick={() => setParModal(null)}>Cancel</button><button className="btn btn-primary" onClick={submitPar}>Save Par Levels</button></div>
        </Modal>
      )}
    </div>
  );
}
